import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { findNikkeInCatalog, nikkeCatalogSearchText } from '../../nikke-catalog/domain/nikkeCatalog.js';
import { useNikkeCatalog } from '../../nikke-catalog/hooks/useNikkeCatalog.js';
import { swapSlots, useDeckSlotDragSwap } from '../hooks/useDeckSlotDragSwap.js';
import { NikkeDeckDisplay } from '../../nikke-catalog/shared/NikkeDeckDisplay.jsx';
import { loadPersonalNikkeData } from '../../personal-nikke/api/personalNikkeApi.js';
import { findPersonalNikke, personalNikkeSuperiorCodeValue } from '../../personal-nikke/domain/personalNikke.js';
import { loadUnionMemberDetail } from '../../union-roster/api/unionMemberDetailApi.js';
import { RaidPracticeNikkeCard } from './RaidPracticeNikkeCard.jsx';
import { RaidPracticeNikkeFilters } from './RaidPracticeNikkeFilters.jsx';
import { RaidPracticeNikkeResultCard } from './RaidPracticeNikkeResultCard.jsx';

const RARE_ORDER = { SSR: 0, SR: 1, R: 2 };
let sharedPersonalLookup = null;
let sharedPersonalPromise = null;
const memberLookupCache = new Map();

function text(value) { return String(value ?? '').trim(); }
function normalizedSearch(value) { return text(value).toLocaleLowerCase('ko-KR').replace(/\s+/g, ''); }
function burstValue(item) {
  if (item?.burst) return item.burst;
  if (item?.burstCode === 'Step1') return '1';
  if (item?.burstCode === 'Step2') return '2';
  if (item?.burstCode === 'Step3') return '3';
  if (item?.burstCode === 'AllStep') return 'A';
  return '-';
}
function candidateSearchText(item) {
  return normalizedSearch(nikkeCatalogSearchText(item));
}
function baseCandidateOrder(left, right) {
  const rareDiff = (RARE_ORDER[left.rare] ?? 9) - (RARE_ORDER[right.rare] ?? 9);
  if (rareDiff) return rareDiff;
  const orderDiff = Number(left.displayOrder || Number.MAX_SAFE_INTEGER) - Number(right.displayOrder || Number.MAX_SAFE_INTEGER);
  return orderDiff || text(left.name).localeCompare(text(right.name), 'ko-KR');
}
function sortCandidates(items, personalLookup, sortMode) {
  return [...items].sort((left, right) => {
    const leftPersonal = findPersonalNikke(personalLookup, left);
    const rightPersonal = findPersonalNikke(personalLookup, right);
    const superiorDiff = personalNikkeSuperiorCodeValue(rightPersonal) - personalNikkeSuperiorCodeValue(leftPersonal);
    const combatDiff = Number(rightPersonal?.combat || 0) - Number(leftPersonal?.combat || 0);
    if (sortMode === 'superior') return superiorDiff || combatDiff || baseCandidateOrder(left, right);
    return combatDiff || superiorDiff || baseCandidateOrder(left, right);
  });
}

export const useSharedNikkeCatalog = useNikkeCatalog;

export function useSharedPersonalNikkeLookup(enabled = true) {
  const [lookup, setLookup] = useState(enabled ? sharedPersonalLookup : null);
  useEffect(() => {
    let active = true;
    if (!enabled) { setLookup(null); return () => { active = false; }; }
    const load = () => {
      if (!sharedPersonalPromise) sharedPersonalPromise = loadPersonalNikkeData().then((result) => {
        sharedPersonalLookup = result?.lookup || null;
        return sharedPersonalLookup;
      }).catch(() => null);
      sharedPersonalPromise.then((result) => { if (active) setLookup(result); });
    };
    const handleChanged = () => { sharedPersonalLookup = null; sharedPersonalPromise = null; load(); };
    load();
    window.addEventListener('dororaid:personal-nikke-changed', handleChanged);
    return () => { active = false; window.removeEventListener('dororaid:personal-nikke-changed', handleChanged); };
  }, [enabled]);
  return lookup;
}

function usePracticeOwnerLookup(memberOpenid = '') {
  const normalizedOpenid = text(memberOpenid);
  const ownLookup = useSharedPersonalNikkeLookup(!normalizedOpenid);
  const [memberLookup, setMemberLookup] = useState(() => memberLookupCache.get(normalizedOpenid) || null);
  const [memberLoading, setMemberLoading] = useState(Boolean(normalizedOpenid && !memberLookupCache.has(normalizedOpenid)));
  const [memberError, setMemberError] = useState('');

  useEffect(() => {
    let active = true;
    if (!normalizedOpenid) { setMemberLookup(null); setMemberLoading(false); setMemberError(''); return () => { active = false; }; }
    const cached = memberLookupCache.get(normalizedOpenid);
    if (cached) { setMemberLookup(cached); setMemberLoading(false); setMemberError(''); return () => { active = false; }; }
    setMemberLoading(true);
    loadUnionMemberDetail(normalizedOpenid).then((detail) => {
      const lookup = detail?.lookup || null;
      // A failed NIKKE read comes back as an empty lookup; caching it would sort this member's roster
      // as unowned for the rest of the session.
      if (!detail?.personalError) memberLookupCache.set(normalizedOpenid, lookup);
      if (active) { setMemberLookup(lookup); setMemberError(detail?.personalError || ''); }
    }).catch((reason) => {
      if (active) { setMemberLookup(null); setMemberError(reason instanceof Error ? reason.message : '대상 멤버의 니케 정보를 불러오지 못했습니다.'); }
    }).finally(() => { if (active) setMemberLoading(false); });
    return () => { active = false; };
  }, [normalizedOpenid]);

  return { lookup: normalizedOpenid ? memberLookup : ownLookup, loading: memberLoading, error: memberError, isMember: Boolean(normalizedOpenid) };
}

export function RaidPracticeDeckDisplayView({ deck = [], mobile = false, catalog = null }) {
  return <NikkeDeckDisplay deck={deck} mobile={mobile} catalog={catalog} />;
}

export function RaidPracticeDeckDisplay({ deck = [], mobile = false }) {
  const { catalog } = useNikkeCatalog();
  return <NikkeDeckDisplay deck={deck} mobile={mobile} catalog={catalog} />;
}

export function RaidPracticeDeckPicker({ value = [], onChange, mobile = false, memberOpenid = '', excludedCodes = [] }) {
  const { catalog, loading, error } = useNikkeCatalog();
  const owner = usePracticeOwnerLookup(memberOpenid);
  const personalLookup = owner.lookup;
  const prefix = mobile ? 'mobile' : 'desktop';
  const deck = Array.from({ length: 5 }, (_, index) => text(value?.[index]));
  // 이미 넣은 니케끼리 끌어서 자리를 바꾼다. 바깥 덱 줄과 선택창 안 스쿼드 줄을 따로 둔다 -
  // 한쪽에서 끌던 것이 다른 쪽에 떨어지면 안 된다.
  const deckDrag = useDeckSlotDragSwap('deck', (a, b) => onChange(swapSlots(deck, a, b)));
  const draftDrag = useDeckSlotDragSwap('draft', (a, b) => {
    setDraftDeck((current) => swapSlots(current, a, b));
    setActiveIndex(b);
  });
  const items = Array.isArray(catalog?.items) ? catalog.items : [];
  const excluded = useMemo(() => new Set((excludedCodes || []).map((code) => text(findNikkeInCatalog(catalog, code)?.nikkeId || code)).filter(Boolean)), [catalog, excludedCodes]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [draftDeck, setDraftDeck] = useState(() => Array(5).fill(''));
  const [query, setQuery] = useState('');
  const [element, setElement] = useState('전체');
  const [burst, setBurst] = useState('전체');
  const [sortMode, setSortMode] = useState('superior');

  const selectedCount = draftDeck.filter(Boolean).length;
  const candidates = useMemo(() => {
    const needle = normalizedSearch(query);
    return sortCandidates(items.filter((item) => {
      if (excluded.has(String(item.nikkeId))) return false;
      if (element !== '전체' && item.element !== element) return false;
      if (burst !== '전체' && burstValue(item) !== burst) return false;
      return !needle || candidateSearchText(item).includes(needle);
    }), personalLookup, sortMode).slice(0, 300);
  }, [burst, element, excluded, items, personalLookup, query, sortMode]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => { if (event.key === 'Escape') setPickerOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => { document.body.style.overflow = previousOverflow; window.removeEventListener('keydown', onKeyDown); };
  }, [pickerOpen]);

  const openPicker = (index) => { setDraftDeck([...deck]); setActiveIndex(index); setPickerOpen(true); };
  const closePicker = () => { setPickerOpen(false); setQuery(''); };
  const clearDraftSlot = (index) => { setDraftDeck((current) => current.map((code, slotIndex) => (slotIndex === index ? '' : code))); setActiveIndex(index); };
  const choose = (item) => {
    if (excluded.has(String(item.nikkeId))) return;
    const selectedIndex = draftDeck.findIndex((code) => findNikkeInCatalog(catalog, code)?.nikkeId === item.nikkeId);
    if (selectedIndex >= 0) { clearDraftSlot(selectedIndex); return; }
    setDraftDeck((current) => {
      const next = [...current];
      next[activeIndex] = item.nikkeId;
      const nextEmpty = Array.from({ length: 5 }, (_, offset) => (activeIndex + offset + 1) % 5).find((index) => !next[index]);
      if (nextEmpty !== undefined) setActiveIndex(nextEmpty);
      return next;
    });
  };
  const applyDraft = () => { onChange([...draftDeck]); closePicker(); };

  if (!loading && !items.length) return <div className={`${prefix}-raid-practice-deck-picker fallback`}><small>{error || '니케 DB가 비어 있습니다.'}</small></div>;

  const pickerModal = pickerOpen ? <div className={`raid-practice-nikke-overlay ${mobile ? 'mobile' : 'desktop'}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closePicker(); }}>
    <section className="raid-practice-nikke-modal multi-select" role="dialog" aria-modal="true" aria-label="스쿼드 니케 선택">
      <header><div><strong>니케 최대 5명 선택</strong><small>{excluded.size ? `다른 덱에서 사용한 ${excluded.size}명 제외` : owner.isMember ? '대상 멤버 니케·오버로드 기준' : '내 니케·오버로드 기준'}</small></div><div className="raid-practice-nikke-modal-actions"><b>{selectedCount}/5</b><button type="button" className="apply" onClick={applyDraft}>적용</button><button type="button" onClick={closePicker} aria-label="닫기">×</button></div></header>
      {owner.loading ? <p className="raid-practice-catalog-status">대상 멤버의 오버로드 정보를 불러오는 중…</p> : null}
      {owner.error ? <p className="raid-practice-catalog-status error">{owner.error}</p> : null}
      <div className="raid-practice-nikke-selection-grid" aria-label="현재 선택한 스쿼드">{draftDeck.map((code, index) => {
        const item = findNikkeInCatalog(catalog, code);
        return <button type="button" key={index} {...draftDrag.slotProps(index)} className={`${activeIndex === index ? 'active' : ''} ${item ? 'selected' : 'empty'} ${draftDrag.slotClass(index)}`.trim()} aria-label={item ? `${index + 1}번 슬롯 ${item.name}. 끌어서 자리를 바꿀 수 있습니다` : `${index + 1}번 빈 캐릭터 슬롯`} onClick={() => { if (draftDrag.shouldIgnoreClick()) return; setActiveIndex(index); }}><RaidPracticeNikkeCard item={item} code={code} slotLabel={`S${index + 1}`} emptyText="+" personal={findPersonalNikke(personalLookup, item)} showPersonalOverload={false} /></button>;
      })}</div>
      <div className="raid-practice-nikke-search"><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="이름·별명·코드 검색" /><select value={sortMode} onChange={(event) => setSortMode(event.target.value)} aria-label="니케 정렬"><option value="superior">우월코드순</option><option value="combat">전투력순</option></select><button type="button" disabled={!draftDeck[activeIndex]} onClick={() => clearDraftSlot(activeIndex)}>현재 슬롯 해제</button><button type="button" disabled={!selectedCount} onClick={() => { setDraftDeck(Array(5).fill('')); setActiveIndex(0); }}>전체 해제</button></div>
      <RaidPracticeNikkeFilters element={element} burst={burst} onElementChange={setElement} onBurstChange={setBurst} />
      <div className="raid-practice-nikke-results">{candidates.map((item) => {
        const selectedIndex = draftDeck.findIndex((code) => findNikkeInCatalog(catalog, code)?.nikkeId === item.nikkeId);
        return <RaidPracticeNikkeResultCard key={item.nikkeId} item={item} personal={findPersonalNikke(personalLookup, item)} selectedIndex={selectedIndex} onSelect={choose} />;
      })}{!candidates.length ? <p>조건에 맞는 니케가 없습니다.</p> : null}</div>
    </section>
  </div> : null;

  return <div className={`${prefix}-raid-practice-deck-picker`}>
    <div className={`${prefix}-raid-practice-deck-slots`}>{deck.map((code, index) => {
      const item = findNikkeInCatalog(catalog, code);
      return <button key={index} type="button" {...deckDrag.slotProps(index)} className={`${item ? 'selected' : 'empty'} ${deckDrag.slotClass(index)}`.trim()} aria-label={item ? `${index + 1}번 슬롯 ${item.name}. 끌어서 자리를 바꿀 수 있습니다` : `${index + 1}번 빈 캐릭터 슬롯`} disabled={loading} onClick={() => { if (deckDrag.shouldIgnoreClick()) return; openPicker(index); }}>{loading ? <span className="raid-practice-nikke-loading" /> : <RaidPracticeNikkeCard item={item} code={code} slotLabel={`S${index + 1}`} personal={findPersonalNikke(personalLookup, item)} showPersonalOverload={false} />}</button>;
    })}</div>
    <small>{excluded.size ? '다른 덱에서 선택한 니케는 사용할 수 없습니다.' : '오버로드 정보는 선택창 카드 우측 상단의 OL 버튼으로 확인합니다.'}</small>
    {owner.error ? <small className={`${prefix}-raid-practice-catalog-error`}>{owner.error}</small> : null}
    {error ? <small className={`${prefix}-raid-practice-catalog-error`}>{error}</small> : null}
    {pickerModal && typeof document !== 'undefined' ? createPortal(pickerModal, document.body) : null}
  </div>;
}
